// src/pages/admin/AdminImpactReport.js
// On-demand impact report — not waiting for a calendar month to pass.
// Built for sharing with sponsors, credit union partners, and grant applications.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { getImpactReport } from '../../lib/db';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const TIER_LABELS = { founding: 'Founding Partner', gold: 'Community Partner', supporter: 'Community Supporter' };

const AdminImpactReport = () => {
  const navigate = useNavigate();
  const [towns, setTowns] = useState([]);
  const [selectedTown, setSelectedTown] = useState('loughrea');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(collection(db, 'towns')).then(snap => {
      setTowns(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);

  useEffect(() => {
    if (!selectedTown) return;
    setLoading(true);
    getImpactReport(selectedTown).then(r => { setReport(r); setLoading(false); });
  }, [selectedTown]);

  const townName = towns.find(t => t.id === selectedTown)?.name || selectedTown;

  const copyAsText = () => {
    if (!report) return;
    const text = `GATHER IMPACT REPORT — ${townName}
Generated ${format(report.generatedAt, 'd MMM yyyy')}

Live events: ${report.liveEvents} (${report.totalEventsEverPosted} posted in total)
RSVPs / interest marks: ${report.totalRSVPs}
Event page views: ${report.totalEventViews}
Registered users: ${report.totalUsers}
Organisers: ${report.organiserCount}
Active services listed: ${report.activeServices}
Skill Share listings: ${report.skillShareListings}
${report.sponsors.length > 0 ? `\nSponsors:\n${report.sponsors.map(s => `- ${s.name} (${TIER_LABELS[s.tier] || s.tier}): ${s.impressions} impressions, ${s.clicks} clicks`).join('\n')}` : ''}`;
    navigator.clipboard.writeText(text);
    toast.success('Copied — paste into an email or application');
  };

  return (
    <div style={s.page}>
      <button onClick={() => navigate('/admin')} style={s.back}>
        <div style={s.backCircle}>←</div>
        <span style={s.backLbl}>Admin</span>
      </button>
      <div style={s.h1}>Impact Report</div>
      <p style={s.subtitle}>Pull real numbers for sponsor conversations, credit union meetings, or grant applications — no waiting for month-end.</p>

      {towns.length > 1 && (
        <select style={s.filterSelect} value={selectedTown} onChange={e => setSelectedTown(e.target.value)}>
          {towns.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}

      {loading || !report ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>
      ) : (
        <>
          <div style={s.reportCard} id="impact-report-printable">
            <div style={s.reportHeader}>
              <div style={s.reportTitle}>{townName}</div>
              <div style={s.reportDate}>Generated {format(report.generatedAt, 'd MMM yyyy')}</div>
            </div>

            <div style={s.statsGrid}>
              <Stat label="Live events" value={report.liveEvents} sub={`${report.totalEventsEverPosted} posted in total`} />
              <Stat label="RSVPs" value={report.totalRSVPs} />
              <Stat label="Event views" value={report.totalEventViews} />
              <Stat label="Registered users" value={report.totalUsers} sub={`${report.organiserCount} organisers`} />
              <Stat label="Services listed" value={report.activeServices} />
              <Stat label="Skill Share listings" value={report.skillShareListings} />
            </div>

            {report.sponsors.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={s.sectionLabel}>Sponsors</div>
                {report.sponsors.map(sp => (
                  <div key={sp.id} style={s.sponsorRow}>
                    <span style={{ fontWeight: 700 }}>{sp.name}</span>
                    <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                      {TIER_LABELS[sp.tier] || sp.tier} · {sp.impressions} impressions · {sp.clicks} clicks
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={copyAsText} style={s.actionBtn}>📋 Copy as text</button>
            <button onClick={() => window.print()} style={s.actionBtn}>🖨️ Print / Save PDF</button>
          </div>
        </>
      )}
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div style={s.statBox}>
    <div style={s.statNum}>{value.toLocaleString()}</div>
    <div style={s.statLbl}>{label}</div>
    {sub && <div style={s.statSub}>{sub}</div>}
  </div>
);

const s = {
  page: { padding: '52px 20px 60px', background: 'var(--cream)', minHeight: '100vh' },
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20 },
  backCircle: { width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)' },
  backLbl: { fontSize: 13, color: 'var(--muted)', fontWeight: 600 },
  h1: { fontSize: 24, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 },
  filterSelect: { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #C8D9C9', fontSize: 13, fontFamily: 'Nunito', background: 'var(--white)', marginBottom: 16, boxSizing: 'border-box' },
  reportCard: { background: 'var(--white)', borderRadius: 18, padding: 22, boxShadow: 'var(--shadow-sm)' },
  reportHeader: { marginBottom: 18, borderBottom: '1px solid #EDEDED', paddingBottom: 14 },
  reportTitle: { fontSize: 20, fontWeight: 800, color: 'var(--ink)' },
  reportDate: { fontSize: 12, color: 'var(--muted)', fontWeight: 600, marginTop: 2 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 },
  statBox: { background: 'var(--sage-pale)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' },
  statNum: { fontSize: 22, fontWeight: 800, color: 'var(--sage-dark)' },
  statLbl: { fontSize: 11, color: 'var(--sage-dark)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 },
  statSub: { fontSize: 10, color: 'var(--sage-dark)', opacity: 0.75, marginTop: 3, fontWeight: 600 },
  sectionLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--muted)', marginBottom: 8 },
  sponsorRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F5F5F5', fontSize: 13 },
  actionBtn: { flex: 1, background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12, padding: '13px 10px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' },
};

export default AdminImpactReport;
